import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {directHoverSelector} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  AnnotationHighlightCommentModel,
  AnnotationHighlightModel,
} from '../../../../models/api/annotationModel';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {AnnotationSummaryContainer} from '../../../common/annotation/annotationSummaryContainer';

/*
 * Props.
 */

interface CommentComposerAnnotationPreviewProps {
  annotation: AnnotationHighlightModel | AnnotationHighlightCommentModel;
  referencedActivityId: number;
  referencedRecipient?: VisualRecipientModel;
  onRemoveAnnotationClick?: Handler;
}

/*
 * Style.
 */

const StyledAnnotationPreviewDiv = styled.div`
  flex: none;
  margin: 3px 3px;
  padding: 6px 6px;
  border: 1px solid ${(p) => p.theme.greys.shade40};
  background-color: ${(p) => p.theme.greys.white};
  border-radius: 12px;
  position: relative;

  & + div {
    border-top: 1px solid ${(p) => p.theme.greys.shade30};
  }
`;

const StyledHoverDiv = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  div:hover > && {
    display: flex;
  }
`;

const StyledActionButton = styled(ChromelessButton)`
  position: absolute;
  right: 3px;
  top: 3px;
`;

const StyledActionDiv = styled.div`
  padding: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};

  ${directHoverSelector} {
    background: ${(p) => p.theme.palette.blue.shade50};
  }
`;

/*
 * Component.
 */

export const CommentComposerAnnotationPreview: FC<CommentComposerAnnotationPreviewProps> = (props) => (
  <StyledAnnotationPreviewDiv>
    <StyledHoverDiv>{maybeRenderRemoveButton(props)}</StyledHoverDiv>
    <AnnotationSummaryContainer
      annotation={props.annotation}
      referencedActivityId={props.referencedActivityId}
      shouldTruncateText
      shouldShowOnOneLine
    />
  </StyledAnnotationPreviewDiv>
);

const maybeRenderRemoveButton = (props: CommentComposerAnnotationPreviewProps) => {
  if (!props.onRemoveAnnotationClick) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledActionButton shouldPreventMouseDown onClick={props.onRemoveAnnotationClick}>
          <StyledActionDiv>
            <Icon name="exitTiny" color={theme.greys.white} />
            <VisuallyHidden>
              <Trans id="remove-annotation">Remove Annotation</Trans>
            </VisuallyHidden>
          </StyledActionDiv>
        </StyledActionButton>
      )}
    </UserThemeConsumer>
  );
};
